/**
 * /payroll/deduction-codes — Payroll deduction code configuration.
 *
 * Shows all deduction codes for the entity with pre-tax flags and
 * GL account mapping. Mirrors _auth.payroll.earning-codes.tsx in layout.
 *
 * Endpoint: GET /api/payroll/deduction-codes
 * Fields from PayrollDeductionCodeOut:
 *   id, code, name, category,
 *   pre_tax_federal, pre_tax_state, pre_tax_fica,
 *   is_employer_match, gl_account_code, is_active
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { queryOptions } from "@tanstack/react-query";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll/deduction-codes")({
  component: DeductionCodesPage,
});

// ── Types ────────────────────────────────────────────────────────────────────
interface DeductionCode {
  id: string;
  code: string;
  name: string;
  category: string;
  pre_tax_federal: boolean;
  pre_tax_state: boolean;
  pre_tax_fica: boolean;
  is_employer_match: boolean;
  gl_account_code: string | null;
  is_active: boolean;
}

// ── Friendly category labels ─────────────────────────────────────────────────
const CATEGORY_LABEL: Record<string, string> = {
  "401k": "401(k)",
  roth_401k: "Roth 401(k)",
  hsa: "HSA",
  health_pre: "Medical (Pre-Tax)",
  health_post: "Medical (Post-Tax)",
  dental: "Dental",
  vision: "Vision",
  garnishment: "Garnishment",
  child_support: "Child Support",
  union_dues: "Union Dues",
  other: "Other",
};

// ── Query ────────────────────────────────────────────────────────────────────
const deductionCodesQueryOptions = () =>
  queryOptions({
    queryKey: ["deduction-codes"],
    queryFn: () => api<DeductionCode[]>("/api/payroll/deduction-codes"),
  });

// ── Helpers ──────────────────────────────────────────────────────────────────
function TaxFlag({ value, tooltip }: { value: boolean; tooltip?: string }) {
  const chip = value ? (
    <Chip label="Pre-tax" size="small" color="success" variant="outlined" sx={{ minWidth: 64, fontSize: 11 }} />
  ) : (
    <Typography variant="body2" color="text.disabled" align="center">
      —
    </Typography>
  );
  return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
}

function CategoryChip({ category }: { category: string }) {
  return (
    <Chip
      label={CATEGORY_LABEL[category] ?? category}
      size="small"
      variant="outlined"
      sx={{ fontSize: 11, borderColor: "divider", color: "text.secondary" }}
    />
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
function DeductionCodesPage() {
  const q = useQuery(deductionCodesQueryOptions());
  const codes = q.data ?? [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <AccountBalanceIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>
          Deduction Codes
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          Pre-tax flags · GL mapping · category
        </Typography>
        {!q.isLoading && (
          <Chip
            label={`${codes.length} code${codes.length === 1 ? "" : "s"}`}
            size="small"
            variant="outlined"
            sx={{ ml: 1 }}
          />
        )}
        <Box sx={{ ml: "auto" }}>
          <HelpHint docPath="payroll-l1/codes.md" label="Deduction codes help" />
        </Box>
      </Box>

      {q.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : q.isError ? (
        <Typography color="error.main" variant="body2">
          Error loading deduction codes: {(q.error as Error).message}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    fontWeight: 700,
                    bgcolor: "action.hover",
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  },
                }}
              >
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="center">
                  <Tooltip title="Reduces federal taxable wages">
                    <span>Fed Pre-Tax</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Reduces state taxable wages">
                    <span>State Pre-Tax</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Reduces FICA (Social Security + Medicare) wages">
                    <span>FICA Pre-Tax</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Employer contributes a matching amount">
                    <span>Employer Match</span>
                  </Tooltip>
                </TableCell>
                <TableCell>GL Account</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {codes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    No deduction codes configured. They are seeded during payroll setup.
                  </TableCell>
                </TableRow>
              )}
              {codes.map((dc) => (
                <TableRow key={dc.id} hover>
                  <TableCell sx={{ fontFamily: "monospace", fontWeight: 600, fontSize: 13 }}>
                    {dc.code}
                  </TableCell>
                  <TableCell>{dc.name}</TableCell>
                  <TableCell>
                    <CategoryChip category={dc.category} />
                  </TableCell>
                  <TableCell align="center">
                    <TaxFlag value={dc.pre_tax_federal} />
                  </TableCell>
                  <TableCell align="center">
                    <TaxFlag value={dc.pre_tax_state} />
                  </TableCell>
                  <TableCell align="center">
                    <TaxFlag value={dc.pre_tax_fica} />
                  </TableCell>
                  <TableCell align="center">
                    {dc.is_employer_match ? (
                      <Chip
                        label="Match"
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ minWidth: 48, fontSize: 11 }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.disabled" align="center">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{ fontFamily: "monospace", color: "text.secondary", fontSize: 12 }}
                  >
                    {dc.gl_account_code ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={dc.is_active ? "Active" : "Inactive"}
                      size="small"
                      color={dc.is_active ? "success" : "default"}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
